import { useLayoutEffect, useState, type RefObject } from 'react';
import { PX_PER_INCH, THERMAL_PAGE_HEIGHT_IN } from './thermalPrintConstants';

export interface UsePrintPaginationParams<T> {
    data: T[];
    renderRow: (row: T) => string;
    measureRowHeights?: (
        data: T[],
        container: HTMLElement,
        renderRow: (row: T) => string,
    ) => number[];
    pageHeightIn?: number;
    padding?: number;
    headerRef: RefObject<HTMLElement | null>;
    continuationHeaderRef?: RefObject<HTMLElement | null>;
    footerRef: RefObject<HTMLElement | null>;
    footerPeelOnlyRef?: RefObject<HTMLElement | null>;
    measureContainerRef: RefObject<HTMLDivElement | null>;
    verifyPostProcess?: (chunks: T[][]) => T[][];
}

function measureHeight(element: HTMLElement | null): number {
    if (!element) {
        return 0;
    }
    return element.getBoundingClientRect().height;
}

async function waitForAssets(): Promise<void> {
    if (typeof document === 'undefined') {
        return;
    }
    if (document.fonts?.ready) {
        await document.fonts.ready;
    }
    const images = Array.from(document.images || []);
    await Promise.all(
        images
            .filter((img) => !img.complete)
            .map(
                (img) =>
                    new Promise<void>((resolve) => {
                        img.addEventListener('load', () => resolve(), { once: true });
                        img.addEventListener('error', () => resolve(), { once: true });
                    })
            )
    );
}

function splitIntoPages<T>(
    data: T[],
    rowHeights: number[],
    maxHeightFirst: number,
    maxHeightCont: number,
): T[][] {
    const result: T[][] = [];
    let currentPage: T[] = [];
    let currentHeight = 0;
    let activeMaxHeight = maxHeightFirst;

    for (let i = 0; i < data.length; i++) {
        const height = rowHeights[i] ?? 0;

        if (currentHeight + height > activeMaxHeight && currentPage.length > 0) {
            result.push(currentPage);
            currentPage = [];
            currentHeight = 0;
            activeMaxHeight = maxHeightCont > 0 ? maxHeightCont : maxHeightFirst;
        }

        currentPage.push(data[i]);
        currentHeight += height;
    }

    if (currentPage.length) {
        result.push(currentPage);
    }

    while (result.length > 0 && result[result.length - 1].length === 0) {
        result.pop();
    }

    return result.length ? result : [[]];
}

/**
 * Paginates `data` into page chunks by measuring each row in a hidden table under
 * `measureContainerRef`, using measured chrome heights. Continuation pages use
 * `footerPeelOnlyRef` when provided so row budgets match pages without totals (less wasted space).
 *
 * Multi-page packing slips omit order totals on non-final pages; the first pass uses the full
 * measured footer (totals + peel). When that yields multiple pages and a peel-only footer ref
 * exists, we re-split using a larger first-page body budget (peel only) so line items can use
 * space that totals would occupy on page 1. Single-page results from that re-split are accepted
 * only if header + all rows + full footer still fit on one sheet.
 *
 * When `verifyPostProcess` is set (e.g. thermal DOM render-and-verify), it runs on the final
 * chunk list before `setPages` so extra rows can be pulled onto earlier pages only if verified.
 */
export function usePrintPagination<T>({
    data = [],
    renderRow,
    measureRowHeights,
    pageHeightIn = THERMAL_PAGE_HEIGHT_IN,
    padding = 20,
    headerRef,
    continuationHeaderRef,
    footerRef,
    footerPeelOnlyRef,
    measureContainerRef,
    verifyPostProcess,
}: UsePrintPaginationParams<T>): { pages: T[][] } {
    const [pages, setPages] = useState<T[][]>([]);

    useLayoutEffect(() => {
        if (!data.length) {
            setPages([[]]);
            return;
        }

        let cancelled = false;
        setPages([]);

        const paginate = async () => {
            await waitForAssets();

            const PAGE_HEIGHT = pageHeightIn * PX_PER_INCH;
            const headerHeightFirst = measureHeight(headerRef.current);
            const headerHeightCont = continuationHeaderRef?.current
                ? measureHeight(continuationHeaderRef.current)
                : headerHeightFirst;
            const footerHeightFull = measureHeight(footerRef.current);
            const footerHeightPeel = footerPeelOnlyRef?.current
                ? measureHeight(footerPeelOnlyRef.current)
                : footerHeightFull;
            const maxHeightFirstConservative = PAGE_HEIGHT - headerHeightFirst - footerHeightFull - padding;
            let maxHeightCont = PAGE_HEIGHT - headerHeightCont - footerHeightPeel - padding;
            if (maxHeightCont <= 0) {
                maxHeightCont =
                    maxHeightFirstConservative > 0
                        ? maxHeightFirstConservative
                        : Math.max(PAGE_HEIGHT - headerHeightCont - footerHeightFull - padding, 1);
            }

            const container = measureContainerRef.current;
            if (!container || maxHeightFirstConservative <= 0) {
                if (!cancelled) {
                    setPages([data]);
                }
                return;
            }

            const rowHeights =
                measureRowHeights?.(data, container, renderRow) ??
                (() => {
                    const heights: number[] = [];
                    for (let i = 0; i < data.length; i++) {
                        const row = data[i];

                        const wrapper = document.createElement('table');
                        wrapper.style.visibility = 'hidden';
                        wrapper.style.position = 'absolute';
                        wrapper.style.pointerEvents = 'none';
                        wrapper.style.width = '100%';

                        const tbody = document.createElement('tbody');
                        wrapper.appendChild(tbody);

                        const tr = document.createElement('tr');
                        tr.innerHTML = renderRow(row);
                        tbody.appendChild(tr);
                        container.appendChild(wrapper);

                        heights.push(wrapper.getBoundingClientRect().height);
                        wrapper.remove();
                    }
                    return heights;
                })();

            let result = splitIntoPages(data, rowHeights, maxHeightFirstConservative, maxHeightCont);

            const canTightenFirstPage =
                result.length > 1 &&
                footerPeelOnlyRef?.current &&
                footerHeightPeel < footerHeightFull;

            if (canTightenFirstPage) {
                const maxHeightFirstAggressive =
                    PAGE_HEIGHT - headerHeightFirst - footerHeightPeel - padding;
                if (maxHeightFirstAggressive > maxHeightFirstConservative) {
                    const aggressive = splitIntoPages(data, rowHeights, maxHeightFirstAggressive, maxHeightCont);
                    if (aggressive.length > 1) {
                        result = aggressive;
                    } else {
                        const totalBody = rowHeights.reduce((a, b) => a + b, 0);
                        const oneSheetNeeded =
                            headerHeightFirst + totalBody + footerHeightFull + padding;
                        if (oneSheetNeeded <= PAGE_HEIGHT) {
                            result = aggressive;
                        }
                    }
                }
            }

            if (!cancelled) {
                let out = result.length ? result : [[]];
                if (verifyPostProcess && out.some((c) => c.length > 0)) {
                    out = verifyPostProcess(out);
                }
                setPages(out.length ? out : [[]]);
            }
        };

        void paginate();

        return () => {
            cancelled = true;
        };
    }, [
        data,
        renderRow,
        measureRowHeights,
        pageHeightIn,
        padding,
        headerRef,
        continuationHeaderRef,
        footerRef,
        footerPeelOnlyRef,
        measureContainerRef,
        verifyPostProcess,
    ]);

    return { pages };
}
